import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

// api / model
import { useAppSelector } from '../../hooks/hooks';
import ExamApi from '../../api/exam.api';
import { Exam } from '../../models/exam';

//comps
import { Box, CircularProgress } from '@mui/material';
import GlobalButton from '../../components/global/button';
import FilterContainer from '../../components/layout/filterContainer';
import GlobalInput from '../../components/global/input';
import GlobalDropdown from '../../components/global/dropdown';
import DataGrid from '../../components/grid/dataGrid';
import ContentContainer from '../../components/layout/contentContainer';
import { CustomTableInputCell, CustomTableDropdownCell, CustomUploadCell } from '../../components/grid/CustomTableCell';

//icons
import { ReactComponent as PlusIcon } from '../../assets/icons/plus-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';

const Exams = () => {
    const appState = useAppSelector((state) => state.appState);
    const _examApi = new ExamApi();
    const termSchoolId = appState?.term?.termSchoolId;

    const [filters, setFilters] = useState({
        title: '',
        exam: {
            id: null,
            name: '',
        },
    });
    const [exams, setExams] = useState([]);
    const [examTypes, setExamTypes] = useState([]);
    const [resetPage, setResetPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFileUploaded, setIsFileUploaded] = useState(false);

    const loadExamData = async (termSchoolId: number) => {
        const exams = await _examApi.GetExams(termSchoolId);
        const types = await _examApi.GetExamTypes();
        setExams(exams);
        setExamTypes(types);
    };

    useEffect(() => {
        if (termSchoolId)
            loadExamData(termSchoolId)
                .catch((error) => toast.error('Error loading exams'))
                .finally(() => setIsLoading(false));
    }, [appState]);

    const handleAddExam = () => {
        const isInEdit = exams.find((exam) => exam.isEditMode);
        if (!isInEdit) {
            setExams([
                {
                    id: 0,
                    examFileId: 0,
                    // examFileName: null,
                    examType: {
                        id: 0,
                        name: '',
                    },
                    termSchoolId,
                    title: '',
                    isEditMode: true,
                },
                ...exams,
            ]);
        }
        setResetPage(true);
    };

    const addExam = async (exam) => {
        let hasEmptyField = false;

        if (!exam.title.trim()) {
            //add toast
            hasEmptyField = true;
        }

        if (exam.examType.id === 0) {
            //add toast
            hasEmptyField = true;
        }

        //add more conditionals if needed
        if (exam.examFileID === 0) {
            //add toast
            hasEmptyField = true;
        }

        if (hasEmptyField) {
            toast.warning('All fields must be filled to add a new exam.');

            return hasEmptyField;
        }

        try {
            await _examApi.AddNewExam(exam);
            loadExamData(termSchoolId);
            toast.success(`${exam.title} successfully created.`);
        } catch (error) {
            toast.error("Error creating exam");
            hasEmptyField = true;
        }
    };

    const saveExam = async (exam) => {
        let hasEmptyField = false;

        if (!exam.title.trim()) {
            //add toast
            hasEmptyField = true;
        }

        if (exam.examType.id === 0) {
            //add toast
            hasEmptyField = true;
        }

        //add more conditionals if needed
        if (exam.examFileID === 0) {
            //add toast
            hasEmptyField = true;
        }

        if (hasEmptyField) {
            toast.warning('All fields must be filled.');

            return hasEmptyField;
        }

        if (exam.id === 0) {
            addExam(exam);
        } else {
            try {
                await _examApi.UpdateExam(exam);
                loadExamData(termSchoolId);
                toast.success(`Changes to exam ${exam.title} have been successfully saved`);
            } catch (error) {
                toast.error("Error saving exam");
            }
        }
    };

    const deleteExam = async (exam: Exam): Promise<boolean> => {
        try {
            await _examApi.DeleteExam(exam?.id);
            await loadExamData(termSchoolId);
            toast.success(`${exam.title} successfully deleted.`);
            return true;
        } catch (error) {
            toast.error("Error deleting exam");
            return false;
        }
    };

    const handleClearAll = () => {
        let filterObject = { ...filters };
        Object.keys(filterObject).forEach((i) => (filterObject[i] = ''));

        setFilters({
            ...filters,
            title: '',
            exam: {
                id: null,
                name: '',
            },
        });
    };

    const handleCancel = async () => {
        const resetExams = exams.slice(1);
        setExams(resetExams);
    };

    const handleDeleteExamFile = async (examFileId) => {
        if (examFileId !== 0) {
            await _examApi.DeleteExamFile(examFileId);
            setIsFileUploaded((state) => !state);
        }
    };

    const renderExamGrid = (data, onChange) => {
        const {
            examType: { id },
        } = data;
        const examType = examTypes.find((e) => e.id === id);

        const onAddFile = (event: any) => {
            setIsFileUploaded((state) => !state);
            if (event.newState[0].extension !== '.pdf') {
                toast.warn('The file must be a pdf file');
            }
        };

        return (
            <>
                <CustomTableInputCell data={data} name="title" onChange={onChange} placeholder={'Exam Title'} width="15%" />
                <CustomTableDropdownCell
                    items={examTypes}
                    data={data}
                    field="id"
                    name="name"
                    dataItemKey="examType"
                    onChange={onChange}
                    value={examType?.name}
                    width="20%"
                    placeholder={!examType ? 'Select an exam type' : examType.name}
                />
                <CustomUploadCell data={data} displayField={'examFileName'} onAdd={onAddFile} saveUrl={'/exam/uploadSchedule'} onChange={onChange} width="35%" />
            </>
        );
    };

    const handleFilter = (name: string, value: any) => {
        setFilters({
            ...filters,
            [name]: value,
        });
    };

    useEffect(() => {
        if (!Object.values(filters).every((o) => o === '')) {
            setResetPage(true);
        } else setResetPage(false);
    }, [filters]);

    const filterExams = (data) => {
        let newData = data;

        if (filters.title) newData = newData.filter((e) => e.title.toLowerCase().includes(filters.title.toLowerCase()) || e.title === '');

        if (filters.exam.id) newData = newData.filter((e) => e.examType.id === filters.exam.id || e.examType.id === 0);

        return newData;
    };

    return (
        <>
            <FilterContainer sx={{ justifyContent: 'space-between' }}>
                <FlexBox>
                    <GlobalInput label="filter" icon={<FilterIcon />} name={'title'} placeholder="Exam Title" sx={{ maxWidth: '100px' }} value={filters.title} onChange={handleFilter} />
                    <GlobalDropdown placeholder="Exam Type" width="170px" field="id" data={examTypes} name="name" dataItemKey="exam" value={filters?.exam} onChange={handleFilter} />
                    <GlobalButton variant="round" text="clear all" secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAll} />
                </FlexBox>
                <GlobalButton text="add new exam" variant="contained" startIcon={<PlusIcon />} onClick={handleAddExam} />
            </FilterContainer>
            {exams?.length === 0 && isLoading ? (
                <ContentContainer padding={'0px 25px'}>
                    <FlexBox sx={{ justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={100} />
                    </FlexBox>
                </ContentContainer>
            ) : (
                <DataGrid
                    isEdit
                    isDelete
                    isFileUploaded={isFileUploaded}
                    topHeight={15}
                    data={filterExams(exams)}
                    columns={examGridColumns}
                    dataItemKey="id"
                    renderRows={renderExamGrid}
                    handleCancel={handleCancel}
                    extraOnAction={handleDeleteExamFile}
                    onSaveAsync={saveExam}
                    resetPage={resetPage}
                    // deleteDialog props
                    deleteDialogProps={{
                        isWarning: true,
                        buttonText: 'delete',
                        deleteItem: 'the selected exam'
                    }}
                    onDelete={deleteExam}
                />
            )}
        </>
    );
};

export default Exams;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        & > * {
            margin-right: 10px;
        }
    }
`;

const examGridColumns = [
    {
        label: 'Title',
    },
    {
        label: 'Exam Type',
    },
    {
        label: 'File',
    },
];
